import { API_HEAD, BASE_URL } from '@/lib/apiConfig';

export interface Service {
  id: string;
  title: string;
  description: string[];
  price: number;
  image: string;
  imageUrl?: string;
  type: 'web' | 'app' | 'agency';
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GetServicesParams {
  type?: 'web' | 'app' | 'agency';
  page?: number;
  limit?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
}

export interface ServicesResponse {
  success: boolean;
  data: Service[];
  pagination: {
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
  message: string;
}

export interface CreateServiceData {
  title: string;
  description: string[];
  price: number;
  type: 'web' | 'app' | 'agency';
}

export interface ServiceResponse {
  success: boolean;
  data: Service;
  message: string;
}

class ServiceService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Kiểm tra xem endpoint có phải là admin endpoint (yêu cầu auth) không
    const isAdminEndpoint = endpoint.includes('/admin/') || 
                          endpoint.includes('/restore') || 
                          endpoint.includes('/permanent') ||
                          (options.method && options.method !== 'GET');
    
    // Luôn lấy token mới nhất từ localStorage
    let token = typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;
    
    // Nếu là admin endpoint và không có token, throw error
    if (isAdminEndpoint && !token && typeof window !== 'undefined') {
      console.error('❌ No token found for admin endpoint:', endpoint);
      throw new Error('Không có token xác thực. Vui lòng đăng nhập lại.');
    }
    
    // Log token status trong development
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
      console.log(`🔑 Making request to: /services${endpoint}`, { 
        hasToken: !!token, 
        isAdminEndpoint,
        method: options.method || 'GET'
      });
    }
    
    // Nếu là admin endpoint, sử dụng helper với auto-refresh
    if (isAdminEndpoint) {
      const { makeAuthenticatedRequest } = await import('./apiHelper');
      const url = `${API_HEAD}services${endpoint}`;
      const baseUrl = BASE_URL;
      
      try {
        const response = await makeAuthenticatedRequest<T>(url, options, baseUrl);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          
          if (response.status === 401) {
            if (typeof window !== 'undefined') {
              localStorage.removeItem('adminToken');
            }
            throw new Error(errorData.message || 'Không có token xác thực. Vui lòng đăng nhập lại.');
          }
          
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        
        return response.json();
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Lỗi không xác định');
      }
    }
    
    // Public endpoints không cần token
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${API_HEAD}services${endpoint}`, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getServices(params: GetServicesParams = {}): Promise<ServicesResponse> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = queryString ? `?${queryString}` : '';
    
    return this.request<ServicesResponse>(endpoint);
  }

  async getServiceById(id: string): Promise<ServiceResponse> {
    return this.request<ServiceResponse>(`/${id}`);
  }

  async createService(data: CreateServiceData): Promise<ServiceResponse> {
    return this.request<ServiceResponse>('', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateService(id: string, data: Partial<CreateServiceData>): Promise<ServiceResponse> {
    return this.request<ServiceResponse>(`/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteService(id: string): Promise<ServiceResponse> {
    return this.request<ServiceResponse>(`/${id}`, {
      method: 'DELETE',
    });
  }

  // Lấy danh sách dịch vụ trong thùng rác
  async getTrashServices(params: GetServicesParams = {}): Promise<ServicesResponse> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = `/admin/trash${queryString ? `?${queryString}` : ''}`;
    
    return this.request<ServicesResponse>(endpoint);
  }

  // Khôi phục dịch vụ
  async restoreService(id: string): Promise<ServiceResponse> {
    return this.request<ServiceResponse>(`/${id}/restore`, {
      method: 'PATCH',
    });
  }

  // Xóa vĩnh viễn dịch vụ
  async permanentDeleteService(id: string): Promise<ServiceResponse> {
    return this.request<ServiceResponse>(`/${id}/permanent`, {
      method: 'DELETE',
    });
  }
}

const serviceService = new ServiceService();
export default serviceService;

